// The filtering, ordering and fold decisions behind the automated form of the slim dropdown.
//
// These live apart from the component because upstream expresses them as AngularJS filter
// expressions inlined in the template — three different filterings of the same list, two of
// them repeated in a count and a list that must agree — and a filter expression transcribed
// into JSX by eye is the easiest thing in this whole port to get subtly wrong. Nothing here
// touches the DOM, so the repo's own convention applies: decisions get tests, pixels get the
// visual diff.

/** `DropdownGroup` — the two collapsible sections. */
export const DropdownGroup = {
  Jobs: 'jobs',
  Alerts: 'alerts',
} as const
export type DropdownGroup = (typeof DropdownGroup)[keyof typeof DropdownGroup]

export type GroupState = Record<DropdownGroup, boolean>

/** `groupState` at `$onInit` (component:103): jobs collapsed, searches open. */
export const INITIAL_GROUP_STATE: GroupState = {
  [DropdownGroup.Jobs]: false,
  [DropdownGroup.Alerts]: true,
}

/**
 * What typing in the search box does to the two folds (component:305).
 *
 * ⚠️ IT IS NOT "EXPAND WHAT MATCHES". The expression is
 * `(!searchInput && group === Alerts) || !!searchInput`, so ANY input opens BOTH groups and
 * clearing the box returns to jobs-closed / searches-open — regardless of what either group
 * contains. A version that opened only the groups with results would look more helpful and
 * would disagree with production on the empty case, where upstream shows two open, empty
 * groups and tells the user their search matched nothing in either.
 */
export function groupStateForSearch(searchInput: string): GroupState {
  const searching = searchInput.length > 0
  return {
    // `(!input && group === Alerts) || !!input` with group = Jobs is `!!input`.
    [DropdownGroup.Jobs]: searching,
    // …and with group = Alerts it is `!input || !!input`, which is a TAUTOLOGY: the searches
    // group is always open. Written as `true` rather than as `searching || true`, which
    // compiles to the same thing while implying the input matters. Whether upstream meant this
    // is a question for upstream; the port reproduces what it does.
    [DropdownGroup.Alerts]: true,
  }
}

export interface FilterableAlert {
  id: number
  name: string
  hostSite?: string | null
  extensionJobId?: number | null
  /** Epoch millis — see `ExtensionAlert.creationDate`. */
  creationDate?: number
}

/** `searchFilterAlertInternal` (component:290) — case-insensitive substring on the name. */
export function matchesSearch(alert: FilterableAlert, searchInput: string): boolean {
  if (searchInput.length === 0) return true
  return alert.name.toLowerCase().includes(searchInput.toLowerCase())
}

/**
 * The "My searches" list: alerts with NO `extensionJobId`, matching the search, minus the one
 * already open, ordered by `alertSorter` then by creation date descending.
 *
 * ⚠️ `filter: { extensionJobId: '!' }` MEANS "HAS NO extensionJobId". AngularJS's object filter
 * reads a leading `!` as negation and an empty remainder as "any value", so the expression
 * selects alerts whose `extensionJobId` is absent — it is not a comparison against the string
 * "!". Read as an ordinary predicate it would select everything, and the group would list the
 * job-bound alerts that the Jobs group above it is already showing.
 *
 * ⚠️ `alertSorter` RETURNS 0 FOR A HOST-SITE ALERT AND 1 OTHERWISE (component:299), so imported
 * searches sort FIRST. It reads like a boolean and is a sort key.
 */
export function mySearches<T extends FilterableAlert>(
  alerts: readonly T[],
  searchInput: string,
  currentAlertId?: number,
): T[] {
  return alerts
    .filter((alert) => alert.extensionJobId === undefined || alert.extensionJobId === null)
    .filter((alert) => matchesSearch(alert, searchInput))
    .filter((alert) => alert.id !== currentAlertId)
    .sort((a, b) => {
      const byHost = alertSortKey(a) - alertSortKey(b)
      if (byHost !== 0) return byHost
      // `orderBy : alertSorter : false : 'creationDate' : 'reverse'` — newest first within a
      // group. A NUMERIC compare, because the field is epoch millis; `localeCompare` on it
      // threw and took the frame down. Missing dates sort last rather than first, which is
      // what 0 would do.
      return (b.creationDate ?? 0) - (a.creationDate ?? 0)
    })
}

function alertSortKey(alert: FilterableAlert): number {
  return typeof alert.hostSite === 'string' && alert.hostSite.length > 0 ? 0 : 1
}

/**
 * The Jobs group's rows: every result except the job the current alert is already bound to.
 *
 * The count beside the header is `these.length + '/' + totalResultCount` — a filtered numerator
 * over an UNfiltered denominator, which is why the two are computed separately rather than one
 * being derived from the other.
 */
export function jobRows<T extends { id: number }>(results: readonly T[], currentExtensionJobId?: number | null): T[] {
  return results.filter((job) => job.id !== currentExtensionJobId)
}
